import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Button,
  IconButton,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Heading,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Divider,
  Badge,
  Spinner,
  Select,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Code,
  List,
  ListItem,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiMic,
  FiUpload,
  FiSend,
  FiMenu,
  FiUser,
  FiLogOut,
  FiCopy,
  FiMicOff,
  FiClock,
  FiCalendar,
  FiEdit3,
  FiSave,
  FiSearch,
  FiFilter,
  FiCheck,
  FiRefreshCw,
  FiInfo,
  FiPlus,
  FiMaximize2,
  FiShare2,
  FiPrinter,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { EndpointsService, Endpoint } from '../../services/endpoints';

// Interface para as consultas
interface Consulta {
  id: string;
  nome_paciente: string;
  prompt_usuario: string;
  resposta_ia: string;
  data_hora: string;
  editada?: boolean;
  consulta_original_id?: string;
}

// Interfaces para Speech Recognition
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onstart: () => void;
  onend: () => void;
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

// Interface para dados complementares
interface DadosComplementares {
  altura: string;
  peso: string;
  imc: string;
  sexo: string;
  pa: string;
  fc: string;
  tax: string;
  sat: string;
  medicamentos: string;
  alergias: string;
  observacoes: string;
}

export const DiagnosticoPage: React.FC = () => {
  const { usuario, logout } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isConfirmModalOpen, onOpen: onConfirmModalOpen, onClose: onConfirmModalClose } = useDisclosure();
  const { isOpen: isExpandedModalOpen, onOpen: onExpandedModalOpen, onClose: onExpandedModalClose } = useDisclosure();
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();

  // Color mode values para o modal expandido
  const modalBgColor = useColorModeValue('gray.50', 'gray.700');
  const modalBorderColor = useColorModeValue('gray.200', 'gray.600');
  const modalTextColor = useColorModeValue('gray.800', 'gray.200');
  const modalPlaceholderColor = useColorModeValue('gray.500', 'gray.400');
  const modalCodeBackgroundColor = useColorModeValue('gray.100', 'gray.600');

  // Estados principais
  const [nomePaciente, setNomePaciente] = useState('');
  const [prompt, setPrompt] = useState('');
  const [respostaIA, setRespostaIA] = useState('');
  const [jsonBruto, setJsonBruto] = useState(''); // Estado para armazenar JSON bruto
  const [isLoading, setIsLoading] = useState(false);
  const [arquivosSelecionados, setArquivosSelecionados] = useState<File[]>([]);
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [consultaSelecionada, setConsultaSelecionada] = useState<Consulta | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [respostaOriginal, setRespostaOriginal] = useState('');
  const [isHtmlContent, setIsHtmlContent] = useState(false); // Estado para controlar se o conteúdo é HTML

  // Estados para filtros
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('');

  // Estados para paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const consultasPorPagina = 10;

  // Estados para gravação de voz
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const [transcricaoTemporaria, setTranscricaoTemporaria] = useState('');

  // Estados para endpoints
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [endpointSelecionado, setEndpointSelecionado] = useState<Endpoint | null>(null);
  const [isLoadingEndpoints, setIsLoadingEndpoints] = useState(false);

  // Estados para dados complementares
  const [dadosComplementares, setDadosComplementares] = useState<DadosComplementares>({
    altura: '',
    peso: '',
    imc: '',
    sexo: '',
    pa: '',
    fc: '',
    tax: '',
    sat: '',
    medicamentos: '',
    alergias: '',
    observacoes: ''
  });

  // Estados para modal de confirmação
  const [dadosEnvioConfirmacao, setDadosEnvioConfirmacao] = useState<{
    url: string;
    prompt: string;
    body: any;
    tipoAuth: string;
    usuario: string;
    senha: string;
    headers: any;
  } | null>(null);

  // Cores do tema
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const jsonBgColor = useColorModeValue('gray.50', 'gray.700'); // Cor para o JSON
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  // Cores específicas para a área de resposta
  const bgColorResponse = useColorModeValue('gray.50', 'gray.700');
  const borderColorResponse = useColorModeValue('gray.200', 'gray.600');
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const gridTemplateColumns = useBreakpointValue({ 
    base: '1fr', 
    lg: '350px 1fr' 
  });

  // Estados computados
  const isFieldsEnabled = !isLoading;
  const isSendEnabled = prompt.trim().length > 0 && isFieldsEnabled;

  // Função para calcular IMC
  const calcularIMC = useCallback((altura: string, peso: string): string => {
    const alturaNum = parseFloat(altura);
    const pesoNum = parseFloat(peso);
    
    if (alturaNum > 0 && pesoNum > 0) {
      const imc = pesoNum / (alturaNum * alturaNum);
      return imc.toFixed(1);
    }
    return '';
  }, []);

  // Atualizar IMC quando altura ou peso mudarem
  useEffect(() => {
    const imc = calcularIMC(dadosComplementares.altura, dadosComplementares.peso);
    if (imc !== dadosComplementares.imc) {
      setDadosComplementares(prev => ({ ...prev, imc }));
    }
  }, [dadosComplementares.altura, dadosComplementares.peso, calcularIMC, dadosComplementares.imc]);

  // Função para atualizar dados complementares
  const atualizarDadosComplementares = (campo: keyof DadosComplementares, valor: string) => {
    setDadosComplementares(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  // Função para inserir dados complementares no prompt
  const inserirDadosComplementares = () => {
    const dadosTexto = [];
    
    if (dadosComplementares.altura || dadosComplementares.peso || dadosComplementares.imc) {
      dadosTexto.push('\n--- DADOS ANTROPOMÉTRICOS ---');
      if (dadosComplementares.altura) dadosTexto.push(`Altura: ${dadosComplementares.altura}m`);
      if (dadosComplementares.peso) dadosTexto.push(`Peso: ${dadosComplementares.peso}kg`);
      if (dadosComplementares.imc) dadosTexto.push(`IMC: ${dadosComplementares.imc}`);
    }

    if (dadosComplementares.sexo) {
      dadosTexto.push('\n--- DADOS DEMOGRÁFICOS ---');
      dadosTexto.push(`Sexo: ${dadosComplementares.sexo}`);
    }

    if (dadosComplementares.pa || dadosComplementares.fc || dadosComplementares.tax || dadosComplementares.sat) {
      dadosTexto.push('\n--- SINAIS VITAIS ---');
      if (dadosComplementares.pa) dadosTexto.push(`Pressão Arterial: ${dadosComplementares.pa}`);
      if (dadosComplementares.fc) dadosTexto.push(`Frequência Cardíaca: ${dadosComplementares.fc} bpm`);
      if (dadosComplementares.tax) dadosTexto.push(`Temperatura: ${dadosComplementares.tax}°C`);
      if (dadosComplementares.sat) dadosTexto.push(`Saturação O2: ${dadosComplementares.sat}%`);
    }

    if (dadosComplementares.medicamentos || dadosComplementares.alergias || dadosComplementares.observacoes) {
      dadosTexto.push('\n--- HISTÓRICO MÉDICO ---');
      if (dadosComplementares.medicamentos) dadosTexto.push(`Medicamentos em uso: ${dadosComplementares.medicamentos}`);
      if (dadosComplementares.alergias) dadosTexto.push(`Alergias: ${dadosComplementares.alergias}`);
      if (dadosComplementares.observacoes) dadosTexto.push(`Observações: ${dadosComplementares.observacoes}`);
    }

    if (dadosTexto.length > 0) {
      const textoCompleto = dadosTexto.join('\n');
      setPrompt(prev => prev + textoCompleto);
      
      // Limpar dados complementares após inserir
      setDadosComplementares({
        altura: '',
        peso: '',
        imc: '',
        sexo: '',
        pa: '',
        fc: '',
        tax: '',
        sat: '',
        medicamentos: '',
        alergias: '',
        observacoes: ''
      });

      toast({
        title: 'Dados inseridos',
        description: 'Os dados complementares foram adicionados ao prompt.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }

    onModalClose();
  };

  // Carregar endpoints disponíveis
  const carregarEndpoints = useCallback(async () => {
    try {
      setIsLoadingEndpoints(true);
      const endpointsData = await EndpointsService.listar();
      setEndpoints(endpointsData.data);
    } catch (error) {
      console.error('Erro ao carregar endpoints:', error);
    } finally {
      setIsLoadingEndpoints(false);
    }
  }, []);

  // Carregar endpoints na inicialização
  useEffect(() => {
    carregarEndpoints();
  }, [carregarEndpoints]);

  // Selecionar automaticamente o endpoint padrão do usuário médico
  useEffect(() => {
    if (usuario && usuario.endpoint_id && endpoints.length > 0 && !endpointSelecionado) {
      const endpointDoUsuario = endpoints.find(endpoint => endpoint.id === usuario.endpoint_id);
      if (endpointDoUsuario) {
        setEndpointSelecionado(endpointDoUsuario);
      }
    }
  }, [usuario, endpoints, endpointSelecionado]);

  // Função para verificar se deve usar endpoint personalizado
  const deveUsarEndpointPersonalizado = () => {
    return endpointSelecionado !== null;
  };

  // Inicializar Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'pt-BR';

      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Atualizar transcrição temporária em tempo real
        setTranscricaoTemporaria(interimTranscript);

        // Adicionar texto final ao campo de sintomas
        if (finalTranscript) {
          setPrompt(prev => {
            const novoTexto = prev + finalTranscript;
            return novoTexto;
          });
          setTranscricaoTemporaria(''); // Limpar temporária após adicionar ao campo
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Erro no reconhecimento de voz:', event.error);
        setIsRecording(false);
        toast({
          title: 'Erro na gravação',
          description: 'Não foi possível capturar o áudio. Verifique as permissões do microfone.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
        setTranscricaoTemporaria(''); // Limpar transcrição temporária ao parar
      };

      setRecognition(recognitionInstance);
    }
  }, [toast]);

  // Função para alternar gravação
  const toggleRecording = () => {
    if (!recognition) {
      toast({
        title: 'Recurso não disponível',
        description: 'Reconhecimento de voz não é suportado neste navegador.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (isRecording) {
      recognition.stop();
      setIsRecording(false);
      setTranscricaoTemporaria(''); // Limpar ao parar manualmente
    } else {
      setTranscricaoTemporaria(''); // Limpar ao iniciar nova gravação
      recognition.start();
      setIsRecording(true);
    }
  };

  // Carregar consultas
  const carregarConsultas = useCallback(async () => {
    try {
      const response = await api.get('/consultas');
      // A API retorna { data: consultas }, então precisamos acessar response.data.data
      setConsultas(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar consultas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar o histórico de consultas.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Carregar consultas na inicialização
  useEffect(() => {
    carregarConsultas();
  }, [carregarConsultas]);

  // Função para selecionar arquivo
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Função para lidar com mudança de arquivo
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setArquivosSelecionados(prev => [...prev, ...files]);
  };

  // Função para preparar dados de envio e mostrar modal de confirmação
  const prepararEnvio = async () => {
    if (!prompt.trim()) return;

    try {
      // Verificar se há arquivos selecionados para envio de documentos
      if (arquivosSelecionados.length > 0) {
        // Buscar endpoint que termina em "/docs" ou "/docsdev"
        const endpointsResponse = await api.get('/consultas/endpoints');
        const endpoints = endpointsResponse.data.dados || [];
        const endpointDocs = endpoints.find((endpoint: Endpoint) => 
          (endpoint.url.endsWith('/docs') || endpoint.url.endsWith('/docsdev')) && endpoint.ativo
        );

        if (!endpointDocs) {
          toast({
            title: 'Endpoint não configurado',
            description: 'A URL para pesquisa de documentos não está configurada.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        // Preparar dados para modal de confirmação - Upload de documentos
        const arquivo = arquivosSelecionados[0];
        const headers: any = {};

        let tipoAuth = 'Nenhuma';
        let usuario = '';
        let senha = '';

        if (endpointDocs.requer_autenticacao && endpointDocs.usuario && endpointDocs.senha) {
          const tipoAuthLower = endpointDocs.tipo_auth?.toLowerCase() || '';
          usuario = endpointDocs.usuario;
          senha = endpointDocs.senha;
          
          if (tipoAuthLower === 'basic auth' || tipoAuthLower === 'basic') {
            tipoAuth = 'Basic Auth';
            const credentials = btoa(`${endpointDocs.usuario}:${endpointDocs.senha}`);
            headers['Authorization'] = `Basic ${credentials}`;
          } else if (tipoAuthLower === 'bearer token' || tipoAuthLower === 'bearer') {
            tipoAuth = 'Bearer Token';
            headers['Authorization'] = `Bearer ${endpointDocs.senha}`;
          } else if (tipoAuthLower === 'api key') {
            tipoAuth = 'API Key';
            headers['X-API-Key'] = endpointDocs.senha;
          }
        }

        // Preparar dados form-data
        let bodyContent = 'Nenhum arquivo selecionado';
        
        if (arquivo) {
          bodyContent = `Form-data - doc: ${arquivo.name} (${arquivo.size} bytes, tipo: ${arquivo.type}), prompt: ${prompt}`;
        }

        setDadosEnvioConfirmacao({
          url: endpointDocs.url,
          prompt: prompt,
          body: bodyContent,
          tipoAuth,
          usuario,
          senha,
          headers
        });

      } else {
        // Envio normal para endpoint personalizado
        const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const dadosEnvio = {
          text: prompt,
          prompt: usuario?.prompt_ia || '',
          sessionid: sessionId
        };

        if (!endpointSelecionado) {
          toast({
            title: 'Endpoint não selecionado',
            description: 'Selecione um endpoint para enviar a consulta.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        const headers: any = {
          'Content-Type': 'application/json',
        };

        let tipoAuth = 'Nenhuma';
        let usuarioAuth = '';
        let senhaAuth = '';

        if (endpointSelecionado.requer_autenticacao && endpointSelecionado.usuario && endpointSelecionado.senha) {
          const tipoAuthLower = endpointSelecionado.tipo_auth?.toLowerCase() || '';
          usuarioAuth = endpointSelecionado.usuario;
          senhaAuth = endpointSelecionado.senha;
          
          if (tipoAuthLower === 'basic auth' || tipoAuthLower === 'basic') {
            tipoAuth = 'Basic Auth';
            const credentials = btoa(`${endpointSelecionado.usuario}:${endpointSelecionado.senha}`);
            headers['Authorization'] = `Basic ${credentials}`;
          } else if (tipoAuthLower === 'bearer token' || tipoAuthLower === 'bearer') {
            tipoAuth = 'Bearer Token';
            headers['Authorization'] = `Bearer ${endpointSelecionado.senha}`;
          } else if (tipoAuthLower === 'api key') {
            tipoAuth = 'API Key';
            headers['X-API-Key'] = endpointSelecionado.senha;
          }
        }

        setDadosEnvioConfirmacao({
          url: endpointSelecionado.url,
          prompt: usuario?.prompt_ia || '',
          body: dadosEnvio,
          tipoAuth,
          usuario: usuarioAuth,
          senha: senhaAuth,
          headers
        });
      }

      // Chamar diretamente confirmarEnvio sem modal
      await confirmarEnvio();

    } catch (error) {
      console.error('Erro ao preparar envio:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao preparar dados de envio.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Função para confirmar envio
  const confirmarEnvio = async () => {
    if (!prompt.trim()) return;

    setIsLoading(true);
    
    try {
      // Verificar se há arquivos selecionados para envio de documentos
      if (arquivosSelecionados.length > 0) {
        // Buscar endpoint que termina em "/docs" ou "/docsdev"
        const endpointsResponse = await api.get('/consultas/endpoints');
        const endpoints = endpointsResponse.data.dados || [];
        const endpointDocs = endpoints.find((endpoint: Endpoint) => 
          (endpoint.url.endsWith('/docs') || endpoint.url.endsWith('/docsdev')) && endpoint.ativo
        );

        if (!endpointDocs) {
          toast({
            title: 'Endpoint não configurado',
            description: 'A URL para pesquisa de documentos não está configurada.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          setIsLoading(false);
          return;
        }

        // Enviar documento para o endpoint /docs como binary
        const arquivo = arquivosSelecionados[0];
        
        if (!arquivo) {
          toast({
            title: 'Nenhum arquivo selecionado',
            description: 'Selecione um arquivo para enviar.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          setIsLoading(false);
          return;
        }

        const headers: any = {};

        // Configurar autenticação se necessário
        if (endpointDocs.requer_autenticacao && endpointDocs.usuario && endpointDocs.senha) {
          const tipoAuth = endpointDocs.tipo_auth?.toLowerCase() || '';
          
          if (tipoAuth === 'basic auth' || tipoAuth === 'basic') {
            const credentials = btoa(`${endpointDocs.usuario}:${endpointDocs.senha}`);
            headers['Authorization'] = `Basic ${credentials}`;
          } else if (tipoAuth === 'bearer token' || tipoAuth === 'bearer') {
            headers['Authorization'] = `Bearer ${endpointDocs.senha}`;
          } else if (tipoAuth === 'api key') {
            headers['X-API-Key'] = endpointDocs.senha;
          }
        }

        // Criar FormData para envio
        const formData = new FormData();
        formData.append('doc', arquivo); // Key 'doc' do tipo file
        formData.append('prompt', prompt); // Key 'prompt' do tipo text com o conteúdo do campo

        const response = await fetch(endpointDocs.url, {
          method: 'POST',
          headers,
          body: formData // Enviar como form-data
        });

        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }

        // Verificar se a resposta tem conteúdo antes de fazer o parsing JSON
        const responseText = await response.text();

        let responseData;
        if (responseText.trim()) {
          try {
            responseData = JSON.parse(responseText);
            
            // JSON de retorno removido conforme solicitado
            
            // Armazenar JSON bruto no estado
            setJsonBruto(JSON.stringify(responseData, null, 2));
            
          } catch (jsonError) {
            responseData = { text: responseText };
            
            // Exibir também para respostas não-JSON
            toast({
              title: '📋 Resposta do Endpoint (Texto)',
              description: `Resposta bruta recebida: ${responseText}`,
              status: 'info',
              duration: 10000,
              isClosable: true,
              position: 'top-right'
            });
            
            // Armazenar resposta de texto no estado
            setJsonBruto(responseText);
          }
        } else {
          responseData = { text: 'Documento processado com sucesso, mas sem resposta do servidor.' };
          
          // Exibir para resposta vazia
          toast({
            title: '📋 Resposta do Endpoint (Vazia)',
            description: 'O servidor retornou uma resposta vazia.',
            status: 'warning',
            duration: 10000,
            isClosable: true,
            position: 'top-right'
          });
          
          // Armazenar informação de resposta vazia
          setJsonBruto('Resposta vazia do servidor');
        }
        
        // Extrair resposta da IA
        let respostaExtraida;
        
        // Se há arquivos selecionados (upload de imagem), extrair o campo 'content' da estrutura choices[0].message.content
        if (arquivosSelecionados.length > 0) {
          respostaExtraida = responseData.choices?.[0]?.message?.content || responseData.content || responseData.output || responseData.response || responseData.text || JSON.stringify(responseData);
        } else {
          // Para consultas sem imagem, usar a lógica original
          respostaExtraida = responseData.output || responseData.response || responseData.text || JSON.stringify(responseData);
        }
        
        // Detectar se o conteúdo contém HTML
        const containsHtml = /<[^>]+>/.test(respostaExtraida);
        setIsHtmlContent(containsHtml);
        
        // Exibir resposta
        setRespostaIA(respostaExtraida);
        
        // Criar data/hora formatada para a consulta
        const dataHoraConsulta = new Date();
        const dataHoraFormatada = dataHoraConsulta.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });

        // Salvar consulta no banco local
        const consultaFormData = new FormData();
        consultaFormData.append('prompt_usuario', prompt);
        consultaFormData.append('nome_paciente', nomePaciente);
        consultaFormData.append('resposta_ia', respostaExtraida);
        consultaFormData.append('endpoint_id', endpointDocs.id);
        consultaFormData.append('data_hora', dataHoraFormatada);
        consultaFormData.append('tempo_resposta', '0');

        await api.post('/consultas', consultaFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Limpar campos e arquivos
        setPrompt('');
        setNomePaciente('');
        setArquivosSelecionados([]);
        
        // Recarregar consultas
        await carregarConsultas();

        // Mensagem de sucesso removida conforme solicitado

        setIsLoading(false);
        return;
      }

      // Gerar sessionid único para esta transação
      const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Preparar dados no formato correto
      const dadosEnvio = {
        text: prompt, // Conteúdo completo do campo "Descreva os sintomas e informações do paciente"
        prompt: usuario?.prompt_ia || '', // Prompt IA do usuário médico
        sessionid: sessionId // ID único para esta transação
      };

      let response;
      
      if (endpointSelecionado) {
        // Enviar para endpoint personalizado
        const headers: any = {
          'Content-Type': 'application/json',
        };
        
        // Configurar autenticação baseada no tipo
        if (endpointSelecionado.requer_autenticacao && endpointSelecionado.usuario && endpointSelecionado.senha) {
          const tipoAuth = endpointSelecionado.tipo_auth?.toLowerCase() || '';
          
          if (tipoAuth === 'basic auth' || tipoAuth === 'basic') {
            const credentials = btoa(`${endpointSelecionado.usuario}:${endpointSelecionado.senha}`);
            headers['Authorization'] = `Basic ${credentials}`;
          } else if (tipoAuth === 'bearer token' || tipoAuth === 'bearer') {
            headers['Authorization'] = `Bearer ${endpointSelecionado.senha}`;
          } else if (tipoAuth === 'api key') {
            headers['X-API-Key'] = endpointSelecionado.senha;
          }
        }
        
        const startTime = Date.now();
        
        response = await fetch('/api/proxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            url: endpointSelecionado.url,
            method: 'POST',
            headers,
            body: dadosEnvio
          })
        });
        
        const endTime = Date.now();
        
        if (!response.ok) {
          throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        
        const responseData = await response.json();
        
        // Criar data/hora formatada para a consulta
        const dataHoraConsulta = new Date();
        const dataHoraFormatada = dataHoraConsulta.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });

        // Extrair resposta da IA
        let respostaExtraida;
        
        // Se há arquivos selecionados (upload de imagem), extrair o campo 'content' da estrutura choices[0].message.content
        if (arquivosSelecionados.length > 0) {
          respostaExtraida = responseData.choices?.[0]?.message?.content || responseData.content || responseData.output || responseData.response || responseData.text || JSON.stringify(responseData);
        } else {
          // Para consultas sem imagem, usar a lógica original
          respostaExtraida = responseData.output || responseData.response || responseData.text || JSON.stringify(responseData);
        }
        
        // Detectar se o conteúdo contém HTML
        const containsHtml = /<[^>]+>/.test(respostaExtraida);
        setIsHtmlContent(containsHtml);
        
        // Salvar consulta no banco local com todos os campos necessários
        const formData = new FormData();
        formData.append('prompt_usuario', prompt); // prompt_usuario (texto digitado pelo usuário)
        formData.append('nome_paciente', nomePaciente);
        formData.append('resposta_ia', respostaExtraida);
        formData.append('endpoint_id', endpointSelecionado.id);
        formData.append('session_id', sessionId);
        formData.append('prompt_utilizado', usuario?.prompt_ia || ''); // prompt_utilizado (prompt IA do médico)
        formData.append('data_consulta', dataHoraConsulta.toISOString()); // data_consulta

        await api.post('/consultas', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        // Formatar resposta para exibição com cabeçalho
        const respostaFormatada = `**PACIENTE:** ${nomePaciente}
**DATA/HORA:** ${dataHoraFormatada}

**DIAGNÓSTICO E RECOMENDAÇÕES:**
${respostaExtraida}`;
        
        setRespostaIA(respostaFormatada);
        setRespostaOriginal(respostaFormatada);
      } else {
        // Enviar para endpoint padrão (backend interno)
        const formData = new FormData();
        formData.append('prompt_usuario', prompt);
        formData.append('nome_paciente', nomePaciente);
        formData.append('session_id', sessionId);

        // Adicionar arquivos se houver
        arquivosSelecionados.forEach((arquivo, index) => {
          formData.append(`arquivo_${index}`, arquivo);
        });

        response = await api.post('/consultas', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setRespostaIA(response.data.resposta_ia);
        setConsultaSelecionada(response.data);
        setRespostaOriginal(response.data?.resposta_ia || '');
        
        // Detectar se o conteúdo contém HTML
        const containsHtml = /<[^>]+>/.test(response.data.resposta_ia || '');
        setIsHtmlContent(containsHtml);
      }
      
      // Limpar campos
      setPrompt('');
      setNomePaciente('');
      setArquivosSelecionados([]);
      
      // Recarregar consultas
      await carregarConsultas();

      toast({
        title: 'Consulta realizada',
        description: 'Diagnóstico gerado com sucesso!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error: any) {
      console.error('Erro ao enviar consulta:', error);
      toast({
        title: 'Erro',
        description: error.message || 'Erro ao processar consulta.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para copiar resposta
  const copiarResposta = () => {
    navigator.clipboard.writeText(respostaIA);
    toast({
      title: 'Copiado!',
      description: 'Resposta copiada para a área de transferência.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Função para compartilhar resposta
  const compartilharResposta = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Diagnóstico e Recomendações - MEDICARE',
          text: respostaIA,
        });
      } catch (error) {
        console.log('Compartilhamento cancelado');
      }
    } else {
      // Fallback para navegadores que não suportam Web Share API
      navigator.clipboard.writeText(respostaIA);
      toast({
        title: 'Copiado!',
        description: 'Conteúdo copiado para compartilhar.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Função para copiar resposta do modal
  const copiarRespostaModal = (resposta: string) => {
    navigator.clipboard.writeText(resposta);
    toast({
      title: 'Copiado!',
      description: 'Resposta copiada para a área de transferência.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Função para compartilhar resposta do modal
  const compartilharRespostaModal = async (resposta: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Resposta da IA - MEDICARE',
          text: resposta,
        });
      } catch (error) {
        console.log('Compartilhamento cancelado');
      }
    } else {
      // Fallback para navegadores que não suportam Web Share API
      navigator.clipboard.writeText(resposta);
      toast({
        title: 'Copiado!',
        description: 'Conteúdo copiado para compartilhar.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  // Função para imprimir resposta do modal
  const imprimirRespostaModal = (resposta: string, nomePaciente?: string) => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // Detectar se o conteúdo contém HTML
      const containsHtml = /<[^>]+>/.test(resposta);
      const content = containsHtml ? resposta : resposta.replace(/\n/g, '<br>');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Resposta da IA - MEDICARE</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px; 
                line-height: 1.6;
              }
              h1 { 
                color: #2B6CB0; 
                border-bottom: 2px solid #2B6CB0; 
                padding-bottom: 10px;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
              }
              .content {
                margin-top: 20px;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Resposta da IA - MEDICARE</h1>
              ${nomePaciente ? `<p><strong>Paciente:</strong> ${nomePaciente}</p>` : ''}
              <p><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</p>
            </div>
            <div class="content">
              ${content}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Função para imprimir resposta
  const imprimirResposta = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const content = isHtmlContent ? respostaIA : respostaIA.replace(/\n/g, '<br>');
      printWindow.document.write(`
        <html>
          <head>
            <title>Diagnóstico e Recomendações - MEDICARE</title>
            <style>
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px; 
                line-height: 1.6;
              }
              h1 { 
                color: #2B6CB0; 
                border-bottom: 2px solid #2B6CB0; 
                padding-bottom: 10px;
              }
              .header {
                text-align: center;
                margin-bottom: 30px;
              }
              .content {
                margin-top: 20px;
              }
              @media print {
                body { margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>MEDICARE - Diagnóstico por IA</h1>
              <p><strong>Paciente:</strong> ${nomePaciente || 'Não informado'}</p>
              <p><strong>Data:</strong> ${new Date().toLocaleDateString('pt-BR')}</p>
            </div>
            <div class="content">
              <h2>Diagnóstico e Recomendações:</h2>
              <div>${content}</div>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Função para visualizar consulta em modal
  const visualizarConsulta = (consulta: Consulta) => {
    setConsultaSelecionada(consulta);
    onViewModalOpen();
  };

  // Função para salvar edição
  const salvarEdicao = async () => {
    if (!consultaSelecionada || respostaIA === respostaOriginal) return;

    setIsEditing(true);
    
    try {
      const response = await api.put(`/consultas/${consultaSelecionada.id}`, {
        resposta_ia: respostaIA
      });

      setConsultaSelecionada(response.data);
      setRespostaOriginal(respostaIA);
      await carregarConsultas();

      toast({
        title: 'Salvo!',
        description: 'Alterações salvas com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar as alterações.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsEditing(false);
    }
  };

  // Debounce para salvar automaticamente
  useEffect(() => {
    if (consultaSelecionada && respostaIA !== respostaOriginal) {
      const timer = setTimeout(() => {
        salvarEdicao();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [respostaIA, consultaSelecionada, respostaOriginal]);

  // Filtrar consultas
  const consultasFiltradas = consultas.filter(consulta => {
    const nomeMatch = !filtroNome || 
      consulta.nome_paciente.toLowerCase().includes(filtroNome.toLowerCase());
    
    const dataConsulta = new Date(consulta.data_hora);
    const dataInicioMatch = !filtroDataInicio || 
      dataConsulta >= new Date(filtroDataInicio);
    const dataFimMatch = !filtroDataFim || 
      dataConsulta <= new Date(filtroDataFim + 'T23:59:59');

    let periodoMatch = true;
    if (filtroPeriodo) {
      const hoje = new Date();
      const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
      
      switch (filtroPeriodo) {
        case 'hoje':
          periodoMatch = dataConsulta >= inicioHoje;
          break;
        case 'semana':
          const inicioSemana = new Date(inicioHoje);
          inicioSemana.setDate(inicioHoje.getDate() - 7);
          periodoMatch = dataConsulta >= inicioSemana;
          break;
        case 'mes':
          const inicioMes = new Date(inicioHoje);
          inicioMes.setDate(inicioHoje.getDate() - 30);
          periodoMatch = dataConsulta >= inicioMes;
          break;
      }
    }

    return nomeMatch && dataInicioMatch && dataFimMatch && periodoMatch;
  });

  // Paginação
  const totalPaginas = Math.ceil(consultasFiltradas.length / consultasPorPagina);
  const indiceInicio = (paginaAtual - 1) * consultasPorPagina;
  const indiceFim = indiceInicio + consultasPorPagina;
  const consultasPaginadas = consultasFiltradas.slice(indiceInicio, indiceFim);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroNome, filtroDataInicio, filtroDataFim, filtroPeriodo]);

  // Componente do histórico de consultas
  const HistoricoConsultas = () => (
    <VStack spacing={4} align="stretch" h="100%" maxH="100%">
      <HStack justify="space-between" flexShrink={0}>
        <Heading size="md">Histórico</Heading>
        <IconButton
          aria-label="Atualizar"
          icon={<FiRefreshCw />}
          size="sm"
          variant="ghost"
          onClick={carregarConsultas}
        />
      </HStack>

      {/* Filtros */}
      <VStack spacing={3} align="stretch" flexShrink={0}>
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <FiSearch color="gray" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por paciente..."
            value={filtroNome}
            onChange={(e) => setFiltroNome(e.target.value)}
          />
        </InputGroup>

        <Select
          size="sm"
          placeholder="Período"
          value={filtroPeriodo}
          onChange={(e) => setFiltroPeriodo(e.target.value)}
        >
          <option value="hoje">Hoje</option>
          <option value="semana">Última semana</option>
          <option value="mes">Último mês</option>
        </Select>

        <HStack spacing={2}>
          <Input
            size="sm"
            type="date"
            placeholder="Data início"
            value={filtroDataInicio}
            onChange={(e) => setFiltroDataInicio(e.target.value)}
          />
          <Input
            size="sm"
            type="date"
            placeholder="Data fim"
            value={filtroDataFim}
            onChange={(e) => setFiltroDataFim(e.target.value)}
          />
        </HStack>
      </VStack>

      <Divider flexShrink={0} />

      {/* Paginação */}
      {totalPaginas > 1 && (
        <HStack justify="space-between" align="center" py={2} flexShrink={0}>
          <Button
            size="sm"
            variant="outline"
            isDisabled={paginaAtual === 1}
            onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))}
          >
            ← Anterior
          </Button>
          
          <HStack spacing={1}>
            <Text fontSize="sm" color="gray.600">
              Página {paginaAtual} de {totalPaginas}
            </Text>
          </HStack>
          
          <Button
            size="sm"
            variant="outline"
            isDisabled={paginaAtual === totalPaginas}
            onClick={() => setPaginaAtual(prev => Math.min(totalPaginas, prev + 1))}
          >
            Próxima →
          </Button>
        </HStack>
      )}

      {/* Lista de consultas */}
      <Box flex={1} minH={0} overflowY="auto">
        <VStack spacing={2} align="stretch">
          {consultasPaginadas.map((consulta) => (
            <Card
              key={consulta.id}
              size="sm"
              cursor="pointer"
              onClick={() => visualizarConsulta(consulta)}
              bg={consultaSelecionada?.id === consulta.id ? 'blue.50' : undefined}
              borderColor={consultaSelecionada?.id === consulta.id ? 'blue.200' : undefined}
              _hover={{ bg: 'gray.50' }}
            >
              <CardBody p={3}>
                <VStack align="start" spacing={1}>
                  <HStack justify="space-between" w="100%">
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                      {consulta.nome_paciente}
                    </Text>
                    {consulta.editada && (
                      <Badge colorScheme="orange" size="sm">
                        Editada
                      </Badge>
                    )}
                  </HStack>
                  <HStack spacing={1}>
                    <FiClock size={12} />
                    <Text fontSize="xs" color="gray.500">
                      {new Date(consulta.data_hora).toLocaleString('pt-BR')}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.600" noOfLines={2}>
                    {consulta.prompt_usuario}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ))}
          
          {(!Array.isArray(consultasFiltradas) || consultasFiltradas.length === 0) && (
            <Text textAlign="center" color="gray.500" py={8}>
              {filtroNome || filtroDataInicio || filtroDataFim || filtroPeriodo 
                ? 'Nenhuma consulta encontrada com os filtros aplicados'
                : 'Nenhuma consulta encontrada'}
            </Text>
          )}
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <Box bg={bgColor} minH="100vh">
      {/* Header */}
      <Flex
        as="header"
        align="center"
        justify="space-between"
        wrap="wrap"
        padding={{ base: 2, md: 4 }}
        bg={cardBg}
        borderBottom="1px"
        borderColor={borderColor}
        shadow="sm"
      >
        <HStack spacing={{ base: 2, md: 4 }}>
          {isMobile && (
            <IconButton
              aria-label="Abrir menu"
              icon={<FiMenu />}
              variant="ghost"
              size="sm"
              onClick={onOpen}
            />
          )}
          <Heading size={{ base: "md", md: "lg" }} color="blue.600" noOfLines={1}>
            MEDICARE - Diagnóstico por IA
          </Heading>
        </HStack>

        <HStack spacing={4}>
          {/* Seletor de Endpoint - OCULTO conforme solicitação */}
          {false && (
          <Menu>
            <MenuButton
              as={Button}
              variant="outline"
              size="sm"
              leftIcon={<FiRefreshCw />}
              isLoading={isLoadingEndpoints}
              colorScheme={deveUsarEndpointPersonalizado() ? "orange" : "blue"}
            >
              {endpointSelecionado?.nome || 'Endpoint Padrão'}
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => setEndpointSelecionado(null)}
                bg={!endpointSelecionado ? 'blue.50' : undefined}
              >
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold">Endpoint Padrão</Text>
                  <Text fontSize="xs" color="gray.500">Sistema interno do Medicare</Text>
                </VStack>
              </MenuItem>
              {endpoints.map((endpoint) => (
                <MenuItem
                  key={endpoint.id}
                  onClick={() => setEndpointSelecionado(endpoint)}
                  bg={endpointSelecionado?.id === endpoint.id ? 'orange.50' : undefined}
                >
                  <VStack align="start" spacing={0}>
                    <HStack>
                      <Text fontWeight="bold">{endpoint.nome}</Text>
                      <Badge colorScheme="orange" size="sm">Personalizado</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{endpoint.descricao}</Text>
                  </VStack>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          )}

          {/* Menu do usuário - OCULTO conforme solicitação */}
          {false && (
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              leftIcon={<Avatar size="sm" name={usuario?.nome} />}
            >
              {usuario?.nome}
            </MenuButton>
            <MenuList>
              <MenuItem icon={<FiUser />}>
                Perfil
              </MenuItem>
              <MenuItem icon={<FiLogOut />} onClick={logout}>
                Sair
              </MenuItem>
            </MenuList>
          </Menu>
          )}
        </HStack>
      </Flex>

      {/* Conteúdo Principal */}
      <Container maxW="full" p={{ base: 2, md: 6 }}>
        <Grid templateColumns={gridTemplateColumns} gap={{ base: 3, md: 6 }} h="calc(100vh - 120px)">
          {/* Sidebar - Histórico (Desktop) */}
          {!isMobile && (
            <GridItem>
              <Card h="100%" bg={cardBg}>
                <CardBody p={6} h="100%" display="flex" flexDirection="column">
                  <HistoricoConsultas />
                </CardBody>
              </Card>
            </GridItem>
          )}

          {/* Área Principal */}
          <GridItem>
            <VStack spacing={6} h="100%">
              {/* Card de Input */}
              <Card w="100%" bg={cardBg}>
                <CardBody p={{ base: 3, md: 6 }}>
                  <VStack spacing={{ base: 3, md: 5 }}>
                    {/* Campo Nome do Paciente */}
                    <FormControl>
                      <FormLabel>Nome do Paciente</FormLabel>
                      <Input
                        value={nomePaciente}
                        onChange={(e) => {
                          setNomePaciente(e.target.value);
                          // Limpar o campo de diagnóstico quando o nome do paciente for alterado
                          if (respostaIA) {
                            setRespostaIA('');
                            setRespostaOriginal('');
                            setConsultaSelecionada(null);
                          }
                        }}
                        placeholder="Digite o nome do paciente..."
                        isDisabled={!isFieldsEnabled}
                      />
                    </FormControl>

                    <FormControl h="180px">
                      <FormLabel>Descreva os sintomas e informações do paciente</FormLabel>
                      <Textarea
                        value={prompt + transcricaoTemporaria}
                        onChange={(e) => {
                          // Remover a transcrição temporária do valor antes de atualizar
                          const valorSemTemporaria = e.target.value.replace(transcricaoTemporaria, '');
                          setPrompt(valorSemTemporaria);
                        }}
                        placeholder="Descreva os sintomas, histórico médico e outras informações relevantes..."
                        isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                        resize="none"
                        h="140px"
                        borderColor={isRecording ? "red.300" : undefined}
                        borderWidth={isRecording ? "2px" : "1px"}
                        _focus={{
                          borderColor: isRecording ? "red.500" : "blue.500",
                          boxShadow: isRecording ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-blue-500)"
                        }}
                      />
                      
                      {/* Indicador simples de gravação */}
                      {isRecording && (
                        <HStack spacing={2} mt={2}>
                          <Box
                            w={3}
                            h={3}
                            bg="red.500"
                            borderRadius="full"
                            animation="pulse 1.5s infinite"
                          />
                          <Text fontSize="sm" fontWeight="bold" color="red.600">
                            Gravando... Fale agora
                          </Text>
                        </HStack>
                      )}
                    </FormControl>

                    {/* Arquivos anexados */}
                    {arquivosSelecionados.length > 0 && (
                      <Box w="100%">
                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                          Arquivos anexados:
                        </Text>
                        <VStack spacing={1} align="start">
                          {arquivosSelecionados.map((arquivo, index) => (
                            <Text key={index} fontSize="sm" color="gray.600">
                              {arquivo.name}
                            </Text>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    {/* Botões de ação */}
                    <Box w="100%">
                      {/* Layout para Desktop */}
                      <HStack 
                        spacing={3} 
                        w="100%" 
                        justify="flex-end"
                        display={{ base: "none", md: "flex" }}
                      >
                        <Button
                          leftIcon={isRecording ? <FiMicOff /> : <FiMic />}
                          colorScheme={isRecording ? "red" : "blue"}
                          variant={isRecording ? "solid" : "outline"}
                          isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                          onClick={toggleRecording}
                          size="md"
                        >
                          {isRecording ? "Parar" : "Transcrição"}
                        </Button>
                        
                        <Button
                          leftIcon={<FiUpload />}
                          variant="outline"
                          isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                          onClick={handleFileSelect}
                          size="md"
                        >
                          Envio de Exames
                        </Button>

                        <Button
                          leftIcon={<FiPlus />}
                          variant="outline"
                          size="md"
                          isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                          onClick={onModalOpen}
                        >
                          Dados Complementares
                        </Button>
                        
                        <Button
                          leftIcon={isLoading ? <Spinner size="sm" /> : <FiSend />}
                          colorScheme={deveUsarEndpointPersonalizado() ? "orange" : "blue"}
                          isDisabled={!isSendEnabled || isLoading || !nomePaciente.trim()}
                          onClick={prepararEnvio}
                          isLoading={isLoading}
                          loadingText="Processando..."
                          size="md"
                        >
                          Enviar
                        </Button>
                      </HStack>

                      {/* Layout para Mobile */}
                      <VStack 
                        spacing={2} 
                        w="100%" 
                        align="stretch"
                        display={{ base: "flex", md: "none" }}
                      >
                        <HStack spacing={2} w="100%">
                          <Button
                            leftIcon={isRecording ? <FiMicOff /> : <FiMic />}
                            colorScheme={isRecording ? "red" : "blue"}
                            variant={isRecording ? "solid" : "outline"}
                            isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                            onClick={toggleRecording}
                            size="sm"
                            fontSize="xs"
                            flex={1}
                          >
                            {isRecording ? "Parar" : "Transcrição"}
                          </Button>
                          
                          <Button
                            leftIcon={<FiUpload />}
                            variant="outline"
                            isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                            onClick={handleFileSelect}
                            size="sm"
                            fontSize="xs"
                            flex={1}
                          >
                            Envio de Exames
                          </Button>
                        </HStack>

                        <HStack spacing={2} w="100%">
                          <Button
                            leftIcon={<FiPlus />}
                            variant="outline"
                            size="sm"
                            fontSize="xs"
                            isDisabled={!isFieldsEnabled || !nomePaciente.trim()}
                            onClick={onModalOpen}
                            flex={1}
                          >
                            Dados Complementares
                          </Button>
                          
                          <Button
                            leftIcon={isLoading ? <Spinner size="sm" /> : <FiSend />}
                            colorScheme={deveUsarEndpointPersonalizado() ? "orange" : "blue"}
                            isDisabled={!isSendEnabled || isLoading || !nomePaciente.trim()}
                            onClick={prepararEnvio}
                            isLoading={isLoading}
                            loadingText="Processando..."
                            size="sm"
                            fontSize="xs"
                            flex={1}
                          >
                            Enviar
                          </Button>
                        </HStack>
                      </VStack>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>

              {/* Card de Resposta */}
              <Card w="100%" bg={cardBg} maxH="51%" flex={1}>
                <CardBody p={{ base: 3, md: 6 }} h="100%">
                  <VStack spacing={{ base: 2, md: 4 }} h="100%">
                    <HStack justify="space-between" w="100%">
                      <HStack spacing={2}>
                        <FormLabel m={0}>Diagnóstico e Recomendações</FormLabel>
                        {consultaSelecionada && (
                          <HStack spacing={1}>
                            <FiEdit3 size={14} color="gray" />
                            <Text fontSize="xs" color="gray.500">
                              Editável
                            </Text>
                          </HStack>
                        )}
                        {isEditing && (
                          <HStack spacing={1}>
                            <Spinner size="xs" color="blue.500" />
                            <Text fontSize="xs" color="blue.500">
                              Salvando...
                            </Text>
                          </HStack>
                        )}
                      </HStack>
                      <HStack spacing={{ base: 0.5, md: 1 }} flexWrap="wrap">
                        <Tooltip label="Copiar resposta">
                          <IconButton
                            aria-label="Copiar resposta"
                            icon={<FiCopy />}
                            size={{ base: "xs", md: "sm" }}
                            variant="ghost"
                            isDisabled={!respostaIA}
                            onClick={copiarResposta}
                          />
                        </Tooltip>
                        <Tooltip label="Visualizar expandido">
                          <IconButton
                            aria-label="Visualizar expandido"
                            icon={<FiMaximize2 />}
                            size={{ base: "xs", md: "sm" }}
                            variant="ghost"
                            isDisabled={!respostaIA}
                            onClick={onExpandedModalOpen}
                          />
                        </Tooltip>
                        <Tooltip label="Compartilhar">
                          <IconButton
                            aria-label="Compartilhar resposta"
                            icon={<FiShare2 />}
                            size={{ base: "xs", md: "sm" }}
                            variant="ghost"
                            isDisabled={!respostaIA}
                            onClick={compartilharResposta}
                          />
                        </Tooltip>
                        <Tooltip label="Imprimir">
                          <IconButton
                            aria-label="Imprimir resposta"
                            icon={<FiPrinter />}
                            size="sm"
                            variant="ghost"
                            isDisabled={!respostaIA}
                            onClick={imprimirResposta}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                    
                    
                    {isHtmlContent ? (
                      <Box
                        h="100%"
                        bg={bgColorResponse}
                        borderRadius="md"
                        border="1px solid"
                        borderColor={isEditing ? 'blue.300' : borderColorResponse}
                        p={3}
                        overflow="auto"
                        dangerouslySetInnerHTML={{ __html: respostaIA }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                        }}
                      />
                    ) : (
                      <Textarea
                        value={respostaIA}
                        onChange={(e) => {
                          setRespostaIA(e.target.value);
                        }}
                        placeholder="O diagnóstico e recomendações da IA aparecerão aqui..."
                        resize="none"
                        h="100%"
                        bg={bgColorResponse}
                        borderColor={isEditing ? 'blue.300' : undefined}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                        }}
                      />
                    )}
                  </VStack>
                </CardBody>
              </Card>
              
            </VStack>
          </GridItem>
        </Grid>
      </Container>

      {/* Input de arquivo oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple
        accept="image/*,.pdf,.txt,.doc,.docx"
        style={{ display: 'none' }}
      />

      {/* Drawer Mobile */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>
          <DrawerBody>
            <VStack spacing={6} align="stretch">
              <HistoricoConsultas />
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Modal de Dados Complementares */}
      <Modal isOpen={isModalOpen} onClose={onModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dados Complementares do Paciente</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {/* Dados Antropométricos */}
              <Box w="100%">
                <Text fontWeight="bold" mb={3} color="blue.600">
                  Dados Antropométricos
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Altura (m)</FormLabel>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Ex: 1.75"
                      value={dadosComplementares.altura}
                      onChange={(e) => atualizarDadosComplementares('altura', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Peso (kg)</FormLabel>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Ex: 70.5"
                      value={dadosComplementares.peso}
                      onChange={(e) => atualizarDadosComplementares('peso', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">IMC</FormLabel>
                    <Input
                      value={dadosComplementares.imc}
                      isReadOnly
                      bg="gray.50"
                      placeholder="Calculado automaticamente"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Dados Demográficos */}
              <Box w="100%">
                <Text fontWeight="bold" mb={3} color="blue.600">
                  Dados Demográficos
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Sexo</FormLabel>
                  <Select
                    placeholder="Selecione o sexo"
                    value={dadosComplementares.sexo}
                    onChange={(e) => atualizarDadosComplementares('sexo', e.target.value)}
                  >
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                    <option value="Outro">Outro</option>
                  </Select>
                </FormControl>
              </Box>

              <Divider />

              {/* Sinais Vitais */}
              <Box w="100%">
                <Text fontWeight="bold" mb={3} color="blue.600">
                  Sinais Vitais
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Pressão Arterial</FormLabel>
                    <Input
                      placeholder="Ex: 120x80 mmHg"
                      value={dadosComplementares.pa}
                      onChange={(e) => atualizarDadosComplementares('pa', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Frequência Cardíaca (bpm)</FormLabel>
                    <Input
                      type="number"
                      placeholder="Ex: 72"
                      value={dadosComplementares.fc}
                      onChange={(e) => atualizarDadosComplementares('fc', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Temperatura (°C)</FormLabel>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="Ex: 36.5"
                      value={dadosComplementares.tax}
                      onChange={(e) => atualizarDadosComplementares('tax', e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Saturação O2 (%)</FormLabel>
                    <Input
                      type="number"
                      placeholder="Ex: 98"
                      value={dadosComplementares.sat}
                      onChange={(e) => atualizarDadosComplementares('sat', e.target.value)}
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Histórico Médico */}
              <Box w="100%">
                <Text fontWeight="bold" mb={3} color="blue.600">
                  Histórico Médico
                </Text>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel fontSize="sm">Medicamentos em uso contínuo</FormLabel>
                    <Textarea
                      placeholder="Liste os medicamentos que o paciente usa regularmente..."
                      value={dadosComplementares.medicamentos}
                      onChange={(e) => atualizarDadosComplementares('medicamentos', e.target.value)}
                      rows={3}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Alergias medicamentosas</FormLabel>
                    <Textarea
                      placeholder="Liste alergias conhecidas a medicamentos..."
                      value={dadosComplementares.alergias}
                      onChange={(e) => atualizarDadosComplementares('alergias', e.target.value)}
                      rows={2}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Observações adicionais</FormLabel>
                    <Textarea
                      placeholder="Outras informações relevantes..."
                      value={dadosComplementares.observacoes}
                      onChange={(e) => atualizarDadosComplementares('observacoes', e.target.value)}
                      rows={3}
                    />
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={inserirDadosComplementares}>
              Inserir Dados
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Visualização Expandida */}
      <Modal 
        isOpen={isExpandedModalOpen} 
        onClose={onExpandedModalClose} 
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="80vw" maxH="80vh">
          <ModalHeader>
            <HStack>
              <Text>Diagnóstico e Recomendações - Visualização Expandida</Text>
              <HStack spacing={1} ml="auto">
                <Tooltip label="Copiar resposta">
                  <IconButton
                    aria-label="Copiar resposta"
                    icon={<FiCopy />}
                    size="sm"
                    variant="ghost"
                    onClick={copiarResposta}
                  />
                </Tooltip>
                <Tooltip label="Compartilhar">
                  <IconButton
                    aria-label="Compartilhar resposta"
                    icon={<FiShare2 />}
                    size="sm"
                    variant="ghost"
                    onClick={compartilharResposta}
                  />
                </Tooltip>
                <Tooltip label="Imprimir">
                  <IconButton
                    aria-label="Imprimir resposta"
                    icon={<FiPrinter />}
                    size="sm"
                    variant="ghost"
                    onClick={imprimirResposta}
                  />
                </Tooltip>
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              p={4}
              bg={modalBgColor}
              borderRadius="md"
              minH="60vh"
              maxH="60vh"
              overflowY="auto"
              border="1px solid"
              borderColor={modalBorderColor}
            >
              {respostaIA ? (
                isHtmlContent ? (
                  <Box
                    dangerouslySetInnerHTML={{ __html: respostaIA }}
                    sx={{
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        fontWeight: 'bold',
                        marginBottom: '0.5rem',
                        marginTop: '1rem',
                      },
                      '& h1': { fontSize: '1.5rem' },
                      '& h2': { fontSize: '1.3rem' },
                      '& h3': { fontSize: '1.1rem' },
                      '& p': {
                        marginBottom: '0.5rem',
                        lineHeight: '1.6',
                      },
                      '& ul, & ol': {
                        marginLeft: '1.5rem',
                        marginBottom: '0.5rem',
                      },
                      '& li': {
                        marginBottom: '0.25rem',
                      },
                      '& strong, & b': {
                        fontWeight: 'bold',
                      },
                      '& em, & i': {
                        fontStyle: 'italic',
                      },
                      '& code': {
                        backgroundColor: modalCodeBackgroundColor,
                        padding: '0.125rem 0.25rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.875rem',
                      },
                      '& pre': {
                        backgroundColor: modalCodeBackgroundColor,
                        padding: '0.5rem',
                        borderRadius: '0.25rem',
                        overflowX: 'auto',
                        marginBottom: '0.5rem',
                      },
                    }}
                  />
                ) : (
                  <Text 
                    whiteSpace="pre-wrap" 
                    fontSize="md" 
                    lineHeight="1.6"
                    color={modalTextColor}
                  >
                    {respostaIA}
                  </Text>
                )
              ) : (
                <Text 
                  color={modalPlaceholderColor} 
                  fontStyle="italic"
                  textAlign="center"
                  mt={8}
                >
                  Nenhuma resposta disponível para visualização
                </Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onExpandedModalClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Visualização de Consulta */}
      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detalhes da Consulta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {consultaSelecionada && (
              <VStack spacing={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>Informações Básicas</Text>
                    <VStack align="stretch" spacing={2}>
                      <HStack>
                        <Text fontWeight="semibold" minW="120px">Paciente:</Text>
                        <Text>{consultaSelecionada.nome_paciente}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="semibold" minW="120px">Data/Hora:</Text>
                        <Text>{new Date(consultaSelecionada.data_hora).toLocaleString('pt-BR')}</Text>
                      </HStack>
                      <HStack>
                        <Text fontWeight="semibold" minW="120px">Status:</Text>
                        <Badge colorScheme={consultaSelecionada.editada ? 'orange' : 'green'}>
                          {consultaSelecionada.editada ? 'Editada' : 'Original'}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Box>
                </SimpleGrid>

                <Divider />

                <Box>
                  <Text fontWeight="bold" mb={2}>Prompt do Usuário</Text>
                  <Box
                    p={3}
                    bg={modalBgColor}
                    borderRadius="md"
                    border="1px"
                    borderColor={modalBorderColor}
                  >
                    <Text whiteSpace="pre-wrap">{consultaSelecionada.prompt_usuario}</Text>
                  </Box>
                </Box>

                <Box>
                  <HStack justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold">Resposta da IA</Text>
                    <HStack spacing={2}>
                      <Tooltip label="Copiar resposta" hasArrow>
                        <IconButton
                          aria-label="Copiar resposta"
                          icon={<FiCopy />}
                          size="sm"
                          variant="ghost"
                          onClick={() => copiarRespostaModal(consultaSelecionada.resposta_ia)}
                        />
                      </Tooltip>
                      <Tooltip label="Compartilhar resposta" hasArrow>
                        <IconButton
                          aria-label="Compartilhar resposta"
                          icon={<FiShare2 />}
                          size="sm"
                          variant="ghost"
                          onClick={() => compartilharRespostaModal(consultaSelecionada.resposta_ia)}
                        />
                      </Tooltip>
                      <Tooltip label="Imprimir resposta" hasArrow>
                        <IconButton
                          aria-label="Imprimir resposta"
                          icon={<FiPrinter />}
                          size="sm"
                          variant="ghost"
                          onClick={() => imprimirRespostaModal(consultaSelecionada.resposta_ia, consultaSelecionada.nome_paciente)}
                        />
                      </Tooltip>
                    </HStack>
                  </HStack>
                  <Box
                    p={3}
                    bg={modalBgColor}
                    borderRadius="md"
                    border="1px"
                    borderColor={modalBorderColor}
                  >
                    {(() => {
                      // Detectar se o conteúdo contém HTML
                      const containsHtml = /<[^>]+>/.test(consultaSelecionada.resposta_ia);
                      
                      if (containsHtml) {
                        return (
                          <Box
                            dangerouslySetInnerHTML={{ __html: consultaSelecionada.resposta_ia }}
                          />
                        );
                      } else {
                        return (
                          <Text whiteSpace="pre-wrap">{consultaSelecionada.resposta_ia}</Text>
                        );
                      }
                    })()}
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewModalClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Confirmação de Envio - REMOVIDO */}
    </Box>
  );
};

export default DiagnosticoPage;